/* Aprende Peque — Home sections behaviour (no dependencies) */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------------- Slider ---------------- */
  if (!customElements.get('ap-slider')) {
    class ApSlider extends HTMLElement {
      connectedCallback() {
        if (this._ready) return;
        this._ready = true;
        this.track = this.querySelector('[data-track]');
        if (!this.track) return;
        this.slides = Array.from(this.track.children);
        this.loop = this.dataset.loop === 'true';
        this.index = 0;

        this.prevBtn = this.querySelector('[data-prev]');
        this.nextBtn = this.querySelector('[data-next]');
        this.dotsWrap = this.querySelector('[data-dots]');
        this.pauseBtn = this.querySelector('[data-pause]');
        this.goBtns = Array.from(this.querySelectorAll('[data-go]'));

        if (this.slides.length < 2) {
          this.querySelectorAll('[data-slider-ui]').forEach((el) => (el.hidden = true));
          return;
        }

        this.prevBtn?.addEventListener('click', () => this.step(-1));
        this.nextBtn?.addEventListener('click', () => this.step(1));
        this.goBtns.forEach((b) => b.addEventListener('click', () => this.go(Number(b.dataset.go))));
        this.buildDots();

        this.track.addEventListener('scroll', () => {
          cancelAnimationFrame(this._raf);
          this._raf = requestAnimationFrame(() => this.sync());
        }, { passive: true });
        this.track.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowRight') { e.preventDefault(); this.step(1); }
          if (e.key === 'ArrowLeft') { e.preventDefault(); this.step(-1); }
        });
        window.addEventListener('resize', () => this.sync(), { passive: true });

        this.setupAutoplay();
        this.sync();
      }

      disconnectedCallback() { this.stop(); }

      perView() {
        const first = this.slides[0];
        if (!first || !first.offsetWidth) return 1;
        return Math.max(1, Math.round(this.track.clientWidth / first.offsetWidth));
      }

      maxIndex() { return Math.max(0, this.slides.length - this.perView()); }

      buildDots() {
        if (!this.dotsWrap) return;
        this.dotsWrap.innerHTML = '';
        this.dots = this.slides.map((_, i) => {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'ap-slider__dot';
          b.setAttribute('aria-label', `${this.dataset.slideLabel || 'Slide'} ${i + 1}`);
          b.addEventListener('click', () => this.go(i));
          this.dotsWrap.appendChild(b);
          return b;
        });
      }

      go(i, userInitiated = true) {
        const max = this.maxIndex();
        if (i > max) i = this.loop ? 0 : max;
        if (i < 0) i = this.loop ? max : 0;
        const target = this.slides[i];
        if (!target) return;
        this.track.scrollTo({
          left: target.offsetLeft - this.pad(),
          behavior: reduceMotion.matches ? 'auto' : 'smooth',
        });
        this.index = i;
        this.update();
        if (userInitiated) this.restart();
      }

      step(dir) {
        const pv = this.perView();
        this.go(this.index + dir * pv);
      }

      pad() { return parseFloat(getComputedStyle(this.track).paddingLeft) || 0; }

      sync() {
        const left = this.track.scrollLeft + this.pad();
        let best = 0;
        let bestDist = Infinity;
        this.slides.forEach((s, i) => {
          const d = Math.abs(s.offsetLeft - left);
          if (d < bestDist) { bestDist = d; best = i; }
        });
        const atEnd = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 4;
        this.index = atEnd ? Math.max(best, this.maxIndex()) : best;
        this.update();
      }

      update() {
        const max = this.maxIndex();
        if (!this.loop) {
          if (this.prevBtn) this.prevBtn.disabled = this.index <= 0;
          if (this.nextBtn) this.nextBtn.disabled = this.index >= max;
        }
        this.dots?.forEach((d, i) => d.setAttribute('aria-current', i === this.index ? 'true' : 'false'));
        this.goBtns.forEach((b) => b.setAttribute('aria-current', Number(b.dataset.go) === this.index ? 'true' : 'false'));
        this.slides.forEach((s, i) => {
          const visible = i >= this.index && i < this.index + this.perView();
          s.toggleAttribute('inert', !visible && this.dataset.inert === 'true');
        });
      }

      setupAutoplay() {
        const interval = Number(this.dataset.autoplay || 0);
        if (!interval || reduceMotion.matches) {
          if (this.pauseBtn) this.pauseBtn.hidden = true;
          return;
        }
        this.interval = interval;
        this.userPaused = false;
        this.addEventListener('pointerenter', () => this.stop());
        this.addEventListener('pointerleave', () => this.start());
        this.addEventListener('focusin', () => this.stop());
        this.addEventListener('focusout', () => this.start());
        document.addEventListener('visibilitychange', () => (document.hidden ? this.stop() : this.start()));
        this.pauseBtn?.addEventListener('click', () => {
          this.userPaused = !this.userPaused;
          this.pauseBtn.setAttribute('aria-pressed', String(this.userPaused));
          this.userPaused ? this.stop() : this.start();
        });
        const io = new IntersectionObserver((entries) => {
          entries.forEach((e) => (e.isIntersecting ? this.start() : this.stop()));
        }, { threshold: 0.3 });
        io.observe(this);
      }

      start() {
        if (!this.interval || this.userPaused || this._timer) return;
        this._timer = setInterval(() => this.go(this.index + 1, false), this.interval);
      }

      stop() { clearInterval(this._timer); this._timer = null; }

      restart() { if (this._timer) { this.stop(); this.start(); } }
    }
    customElements.define('ap-slider', ApSlider);
  }

  /* ---------------- Video (YouTube facade / native) ---------------- */
  if (!customElements.get('ap-video')) {
    class ApVideo extends HTMLElement {
      connectedCallback() {
        if (this._ready) return;
        this._ready = true;
        this.frame = this.querySelector('.ap-video');
        this.btn = this.querySelector('[data-play]');
        this.video = this.querySelector('video');
        this.btn?.addEventListener('click', () => this.toggle());
        if (this.video) {
          this.video.addEventListener('ended', () => this.frame.classList.remove('is-playing'));
          this.video.addEventListener('pause', () => this.frame.classList.remove('is-playing'));
          this.video.addEventListener('play', () => this.frame.classList.add('is-playing'));
        }
      }

      toggle() {
        const yt = this.dataset.youtube;
        if (yt) {
          const iframe = document.createElement('iframe');
          iframe.src = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(yt)}?autoplay=1&rel=0&playsinline=1&modestbranding=1`;
          iframe.title = this.dataset.title || 'YouTube video';
          iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
          iframe.allowFullscreen = true;
          this.frame.innerHTML = '';
          this.frame.appendChild(iframe);
          iframe.focus();
          return;
        }
        if (!this.video) return;
        if (this.video.paused) {
          document.querySelectorAll('ap-video video').forEach((v) => v !== this.video && v.pause());
          this.video.controls = true;
          this.frame.classList.add('is-playing');
          this.btn && this.btn.blur();
          this.video.play().catch(() => this.frame.classList.remove('is-playing'));
        } else {
          this.video.pause();
        }
      }
    }
    customElements.define('ap-video', ApVideo);
  }

  /* ---------------- Variant selector (featured product) ---------------- */
  document.addEventListener('change', (e) => {
    const select = e.target.closest('select[data-ap-variant]');
    if (!select) return;
    const opt = select.selectedOptions[0];
    const root = select.closest('[data-ap-product]');
    if (!root || !opt) return;
    const now = root.querySelector('[data-ap-price]');
    const was = root.querySelector('[data-ap-compare]');
    const btn = root.querySelector('button[name="add"]');
    const label = btn?.querySelector('span');
    if (now) now.textContent = opt.dataset.price;
    if (was) {
      was.textContent = opt.dataset.compare || '';
      was.hidden = !opt.dataset.compare;
    }
    if (btn && label) {
      const available = opt.dataset.available === 'true';
      btn.disabled = !available;
      label.textContent = available ? btn.dataset.addLabel : btn.dataset.soldLabel;
    }
    if (opt.dataset.media) {
      const slider = root.querySelector('ap-slider');
      const idx = Number(opt.dataset.media);
      if (slider && typeof slider.go === 'function' && !Number.isNaN(idx)) slider.go(idx);
    }
  });

  /* ---------------- Reveal on scroll ---------------- */
  const initReveal = (scope = document) => {
    const items = scope.querySelectorAll('[data-ap-reveal]:not(.is-visible)');
    if (!items.length) return;
    if (!('IntersectionObserver' in window) || reduceMotion.matches || window.Shopify?.designMode) {
      items.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    document.documentElement.classList.add('ap-reveal-ready');
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
    items.forEach((el) => io.observe(el));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initReveal());
  } else {
    initReveal();
  }
  document.addEventListener('shopify:section:load', (e) => initReveal(e.target));
  /* ---------------- Playful interactions ---------------- */
  const fine = window.matchMedia('(hover: hover) and (pointer: fine)');
  document.addEventListener('pointermove', (e) => {
    if (!fine.matches || reduceMotion.matches) return;
    const card = e.target.closest && e.target.closest('[data-ap-tilt]');
    if (!card) return;
    const r = card.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width - 0.5;
    const y = (e.clientY - r.top) / r.height - 0.5;
    card.style.setProperty('--ry', `${(x * 12).toFixed(2)}deg`);
    card.style.setProperty('--rx', `${(-y * 12).toFixed(2)}deg`);
  }, { passive: true });
  document.addEventListener('pointerout', (e) => {
    const card = e.target.closest && e.target.closest('[data-ap-tilt]');
    if (card && !card.contains(e.relatedTarget)) {
      card.style.setProperty('--rx', '0deg');
      card.style.setProperty('--ry', '0deg');
    }
  });
  const confettiColors = ['#f59d15', '#00a7b5', '#ffdc7d', '#f26b5b', '#007e84', '#ffffff'];
  document.addEventListener('click', (e) => {
    const card = e.target.closest('[data-ap-pop]');
    if (!card) return;
    card.classList.remove('is-popped');
    void card.offsetWidth;
    card.classList.add('is-popped');
    setTimeout(() => card.classList.remove('is-popped'), 950);
    if (reduceMotion.matches) return;
    const r = card.getBoundingClientRect();
    const ox = (e.clientX || r.left + r.width / 2) - r.left;
    const oy = (e.clientY || r.top + r.height / 2) - r.top;
    for (let i = 0; i < 16; i++) {
      const p = document.createElement('span');
      p.className = 'ap-confetti';
      const a = (Math.PI * 2 * i) / 16 + Math.random() * 0.4;
      const d = 60 + Math.random() * 70;
      p.style.left = `${ox}px`;
      p.style.top = `${oy}px`;
      p.style.background = confettiColors[i % confettiColors.length];
      p.style.setProperty('--dx', `${Math.cos(a) * d}px`);
      p.style.setProperty('--dy', `${Math.sin(a) * d - 30}px`);
      p.style.setProperty('--rot', `${Math.random() * 540 - 270}deg`);
      if (i % 3 === 0) p.style.borderRadius = '50%';
      card.appendChild(p);
      setTimeout(() => p.remove(), 950);
    }
  });

  /* ---------------- YouTube poster (no remote img in Liquid) ---------------- */
  const initPosters = (scope = document) => {
    scope.querySelectorAll('ap-video[data-youtube]').forEach((el) => {
      const frame = el.querySelector('.ap-video');
      if (!frame || frame.querySelector('img')) return;
      const img = document.createElement('img');
      img.src = `https://i.ytimg.com/vi/${encodeURIComponent(el.dataset.youtube)}/hqdefault.jpg`;
      img.alt = '';
      img.loading = 'lazy';
      frame.prepend(img);
    });
  };

  /* ---------------- Reputon YouTube widget loader ---------------- */
  const initReputon = () => {
    const widget = document.querySelector('.reputon-youtube-widget');
    if (!widget || document.querySelector('script[data-ap-reputon]')) return;
    const shopEl = widget.closest('[data-shop]');
    const shop = (shopEl && shopEl.dataset.shop) || (window.Shopify && window.Shopify.shop) || '';
    const sc = document.createElement('script');
    sc.src = `https://cdn.ytb.reputon.com/assets/widget.js?shop=${encodeURIComponent(shop)}`;
    sc.defer = true;
    sc.dataset.apReputon = 'true';
    document.body.appendChild(sc);
  };

  const boot = () => { initPosters(); initReputon(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', (e) => { initPosters(e.target); initReputon(); });
})();

/* External links open in a new tab (home) */
(() => {
  const OWN = [location.hostname, 'aprendepeque.com', 'www.aprendepeque.com', 'aprendepeque.myshopify.com'];
  document.addEventListener('click', (e) => {
    const a = e.target.closest && e.target.closest('a[href]');
    if (!a) return;
    let url;
    try { url = new URL(a.getAttribute('href'), location.href); } catch (err) { return; }
    if (!/^https?:$/.test(url.protocol)) return;          // mailto:, tel:, #anchors stay as they are
    if (OWN.includes(url.hostname)) return;               // own domain → same tab
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
  }, true);
})();

/* Home: header logo scrolls to the top instead of reloading the page */
(() => {
  const isHome = location.pathname === '/' || /^\/[a-z]{2}(-[a-z]{2})?\/?$/i.test(location.pathname);
  if (!isHome) return;
  document.addEventListener('click', (e) => {
    const logo = e.target.closest('.header__heading-link');
    if (!logo || e.metaKey || e.ctrlKey || e.shiftKey) return;
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  });
})();